import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Video, Play, Loader2, ExternalLink } from 'lucide-react';
import EmbedWrapper from '@/components/EmbedWrapper';

function extractYouTubeId(text) {
  if (!text) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/playlist\?list=([a-zA-Z0-9_-]+)/,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m) return m[1];
  }
  return null;
}

export default function VideoPlayer({ apiUrl, backendUrl, embed = false }) {
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVideos = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/documents`, { params: { type: 'video', page_size: 100 } });
      setVideos(res.data.documents || []);
      if (res.data.documents?.length > 0) setSelected(res.data.documents[0]);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [apiUrl]);

  useEffect(() => { fetchVideos(); }, [fetchVideos]);

  const getVideoSrc = (v) => {
    if (!v) return null;
    const ytId = extractYouTubeId(v.title) || extractYouTubeId(v.description) || extractYouTubeId(v.file_path);
    if (ytId) return { type: 'youtube', id: ytId };
    return { type: 'file', url: `${backendUrl}/api/uploads/${v.file_path}` };
  };

  const content = (
    <div className="min-h-screen text-slate-200" style={{ background: '#0a0a0f' }} data-testid="video-player">
      <div className={embed ? 'px-4 pt-4 pb-8' : 'max-w-5xl mx-auto px-4 pt-8 pb-12'}>
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.3)' }}>
            <Video className="w-5 h-5" style={{ color: '#d4af37' }} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-white">Federation Video Archive</h1>
            <p className="text-xs text-slate-500">{videos.length} videos</p>
          </div>
        </div>

        {/* Main Player */}
        {selected && (() => {
          const src = getVideoSrc(selected);
          return (
            <div className="rounded-xl overflow-hidden mb-6" style={{ background: 'rgba(212,175,55,0.05)', border: '1px solid rgba(212,175,55,0.15)' }}>
              <div className="aspect-video bg-black flex items-center justify-center">
                {src?.type === 'youtube' ? (
                  <iframe
                    src={`https://www.youtube.com/embed/${src.id}`}
                    className="w-full h-full"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={selected.title}
                    data-testid="youtube-embed"
                  />
                ) : src?.type === 'file' ? (
                  <video src={src.url} controls className="w-full h-full" data-testid="video-element" />
                ) : (
                  <div className="text-center text-slate-500 p-8">
                    <Video className="w-12 h-12 mx-auto mb-3 opacity-30" />
                    <p className="text-sm">Video format requires direct playback</p>
                  </div>
                )}
              </div>
              <div className="p-4">
                <h2 className="text-sm font-medium text-white">{selected.title}</h2>
                <p className="text-[10px] text-slate-500 mt-1">{selected.category} {selected.file_size ? `· ${(selected.file_size / 1048576).toFixed(1)} MB` : ''}</p>
              </div>
            </div>
          );
        })()}

        {/* Grid */}
        {loading ? (
          <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin mx-auto" style={{ color: '#d4af37' }} /></div>
        ) : videos.length === 0 ? (
          <div className="text-center py-16 text-slate-600">
            <Video className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No video content available</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {videos.map((v) => {
              const ytId = extractYouTubeId(v.title) || extractYouTubeId(v.description);
              return (
                <button
                  key={v.id}
                  onClick={() => setSelected(v)}
                  className="text-left rounded-xl overflow-hidden transition-all hover:-translate-y-0.5"
                  style={selected?.id === v.id
                    ? { boxShadow: '0 0 0 2px #d4af37', background: 'rgba(212,175,55,0.05)' }
                    : { background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }
                  }
                  data-testid={`video-thumb-${v.id}`}
                >
                  <div className="aspect-video flex items-center justify-center relative" style={{ background: 'rgba(255,255,255,0.03)' }}>
                    {ytId ? (
                      <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <Play className="w-8 h-8 text-slate-700" />
                    )}
                    {selected?.id === v.id && (
                      <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'rgba(212,175,55,0.15)' }}>
                        <div className="rounded-full p-1.5" style={{ background: '#d4af37' }}>
                          <Play className="w-3 h-3 text-black" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-2.5">
                    <div className="text-xs text-slate-300 line-clamp-2 leading-snug">{v.title}</div>
                    <div className="text-[9px] text-slate-600 mt-1">{v.category}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  if (embed) return <EmbedWrapper module="video">{content}</EmbedWrapper>;
  return content;
}
